import { ShowButton } from "@refinedev/antd";
import { IResourceComponentsProps } from "@refinedev/core";
import {
  Form,
  Input,
  Layout,
  Radio,
  RadioChangeEvent,
  Typography,
  notification,
} from "antd";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PhoneValidator } from "services/user/PhoneValidator";
import { UsernameValidator } from "../../services/user/UsernameValidator";
import { EmailValidator } from "services/user/EmailValidator";
import { getUserUseCase } from "usecases/user/getUserUseCase";

const { Title } = Typography;

export const UserFind: React.FC<IResourceComponentsProps> = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [recordItemId, setRecordItemId] = useState<string | undefined>(
    undefined
  );
  const [inputValue, setInputValue] = useState<string>("");
  const [findValue, setFindValue] = useState("username");

  const onChange = (e: RadioChangeEvent) => {
    setFindValue(e.target.value);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
  };

  const handleFormSubmit = async () => {
    if (findValue === "username") {
      setRecordItemId(inputValue);
    }

    if (findValue === "phone") {
      try {
        const user = await getUserUseCase("phone", inputValue);
        setRecordItemId(user?.data?.username);
      } catch (error: any) {
        if (error.response?.status === 404) {
          notification.error({
            message: "User not found",
            description: "Unable to find user with this phone number.",
          });
        } else {
          notification.error({
            message: error?.response?.data?.message,
            description: error?.response?.status,
          });
        }
      }
    }
    if (findValue === "email") {
      try {
        const user = await getUserUseCase("email", inputValue);
        setRecordItemId(user?.data?.username);
      } catch (error: any) {
        if (error.response?.status === 404) {
          notification.error({
            message: "User not found",
            description: "Unable to find user with this email.",
          });
        } else {
          notification.error({
            message: error?.response?.data?.message,
            description: error?.response?.status,
          });
        }
      }
    }
    if (findValue === "userId") {
      try {
        const user = await getUserUseCase("userId", inputValue);
        setRecordItemId(user?.data?.username);
      } catch (error: any) {
        if (error.response?.status === 404) {
          notification.error({
            message: "User not found",
            description: "Unable to find user with this id.",
          });
        } else {
          notification.error({
            message: error?.response?.data?.message,
            description: error?.response?.status,
          });
        }
      }
    }
  };

  useEffect(() => {
    if (recordItemId) {
      navigate(`/users/show/${recordItemId}`);
      setRecordItemId(undefined);
    }
  }, [recordItemId]);

  return (
    <Layout>
      <Title level={5}>Find:</Title>
      <Radio.Group onChange={onChange} value={findValue}>
        <Radio value="username">by username</Radio>
        <Radio value="phone">by phone</Radio>
        <Radio value="email">by email</Radio>
        <Radio value="userId">by userId</Radio>
      </Radio.Group>
      <Form layout="vertical" form={form}>
        {findValue === "username" && (
          <Form.Item
            name="username"
            rules={[
              {
                required: true,
                validator: async (_, value: string) => {
                  const validator = new UsernameValidator();
                  if (validator.validate(value)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(validator.errorMessage()));
                },
              },
            ]}
          >
            <Input onChange={handleInputChange} />
          </Form.Item>
        )}
        {findValue === "phone" && (
          <Form.Item
            name="phone"
            rules={[
              {
                required: true,
                validator: async (_, value: string) => {
                  const validator = new PhoneValidator();
                  if (validator.validate(value)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(validator.errorMessage()));
                },
              },
            ]}
          >
            <Input onChange={handleInputChange} />
          </Form.Item>
        )}
        {findValue === "email" && (
          <Form.Item
            name="email"
            rules={[
              {
                required: true,
                validator: async (_, value: string) => {
                  const validator = new EmailValidator();
                  if (validator.validate(value)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(validator.errorMessage()));
                },
              },
            ]}
          >
            <Input onChange={handleInputChange} />
          </Form.Item>
        )}
        {findValue === "userId" && (
          <Form.Item
            name="userId"
            rules={[
              {
                required: true,
                validator: async (_, value: string) => {
                  const regex =
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
                  if (!value) {
                    return Promise.reject("User ID is required");
                  }

                  if (!regex.test(value)) {
                    return Promise.reject("Please enter a valid UUID");
                  }
                  if (regex.test(value)) {
                    return Promise.resolve();
                  }
                },
              },
            ]}
          >
            <Input onChange={handleInputChange} />
          </Form.Item>
        )}
      </Form>
      <ShowButton
        size="small"
        onClick={() => {
          handleFormSubmit();
        }}
      />
    </Layout>
  );
};
